import {FC, ReactNode, useEffect, useState} from 'react'
import axios from 'axios'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import toaster from '../../../../Utils/toaster'
import {useAiAssignmentAgentActive} from '../../../hooks/useAiAssignmentFeature'

const API_URL = process.env.REACT_APP_API_URL

const DISTANCE_OPTIONS = [100, 150, 200, 250, 300]

type RebroadcastModalProps = {
  appointmentId: number
  showDistanceRadius?: boolean
  aiAssignmentInScope?: boolean
  onClose: () => void
  onSuccess: () => void
}

type RebroadcastOptionProps = {
  id: string
  checked: boolean
  onChange: (checked: boolean) => void
  title: string
  tooltip: ReactNode
  description: string
}

const RebroadcastOption: FC<RebroadcastOptionProps> = ({
  id,
  checked,
  onChange,
  title,
  tooltip,
  description,
}) => (
  <div
    className='rounded border px-4 py-3'
    style={{
      backgroundColor: checked ? 'rgba(163,162,66,0.06)' : '#f9fafb',
      borderColor: checked ? '#a3a242' : '#f1f1f2',
      transition: 'background-color 0.15s ease, border-color 0.15s ease',
    }}
  >
    <label htmlFor={id} className='d-flex align-items-start gap-3 mb-0 w-100 cursor-pointer'>
      <input
        id={id}
        type='checkbox'
        className='visually-hidden'
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden='true'
        className='d-flex align-items-center justify-content-center flex-shrink-0'
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '6px',
          border: `2px solid ${checked ? '#a3a242' : '#c5c8d4'}`,
          backgroundColor: checked ? '#a3a242' : '#ffffff',
          marginTop: '2px',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
        }}
      >
        {checked && (
          <i
            className='bi bi-check-lg'
            style={{color: '#ffffff', fontSize: '13px', lineHeight: 1}}
          ></i>
        )}
      </span>
      <span className='flex-grow-1'>
        <span className='d-flex align-items-center gap-2'>
          <span className='fw-semibold text-gray-900' style={{fontSize: '0.9rem'}}>
            {title}
          </span>
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip id={`${id}-tooltip`}>{tooltip}</Tooltip>}
          >
            <span
              className='d-inline-flex'
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
            >
              <i className='bi bi-info-circle text-muted'></i>
            </span>
          </OverlayTrigger>
        </span>
        <span className='d-block text-muted mt-1' style={{fontSize: '0.8rem', lineHeight: 1.4}}>
          {description}
        </span>
      </span>
    </label>
  </div>
)

const RebroadcastModal: FC<RebroadcastModalProps> = ({
  appointmentId,
  showDistanceRadius = true,
  aiAssignmentInScope = false,
  onClose,
  onSuccess,
}) => {
  const [distanceMiles, setDistanceMiles] = useState(100)
  const [ignoreServiceType, setIgnoreServiceType] = useState(false)
  const [rerunAiAssignment, setRerunAiAssignment] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const showAiAssignmentRerun = useAiAssignmentAgentActive(aiAssignmentInScope)

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  const handleRebroadcast = async () => {
    setIsSubmitting(true)
    try {
      const response = await axios.post(`${API_URL}/Appoinment/${appointmentId}/rebroadcast`, {
        distanceMiles,
        ignoreServiceType,
        rerunAiAssignment: showAiAssignmentRerun && rerunAiAssignment,
      })
      if (response.data?.status === 'S') {
        toaster('success', response.data?.text ?? 'Rebroadcast initiated successfully.')
        onClose()
        onSuccess()
        setTimeout(() => onSuccess(), 5000)
      } else {
        toaster('error', response.data?.text ?? 'Failed to rebroadcast appointment.')
      }
    } catch (error: any) {
      toaster('error', error?.response?.data?.text ?? 'Failed to rebroadcast appointment.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div
        className='modal fade show d-block'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
        onClick={onClose}
      >
        <div
          className='modal-dialog modal-dialog-centered'
          style={{maxWidth: '520px'}}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='modal-content rounded overflow-hidden'>
            <div className='modal-header border-0 pb-0 px-6 pt-5'>
              <div className='d-flex align-items-center gap-3 flex-grow-1'>
                <div
                  className='d-flex align-items-center justify-content-center rounded'
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(163,162,66,0.14)',
                    color: '#a3a242',
                    fontSize: '18px',
                  }}
                >
                  <i className='bi bi-broadcast-pin'></i>
                </div>
                <div>
                  <h3 className='modal-title fw-bold mb-0 fs-3 text-gray-900'>Rebroadcast Appointment</h3>
                  <p className='text-muted mb-0' style={{fontSize: '0.82rem'}}>
                    Re-run interpreter matching &amp; notify by email
                  </p>
                </div>
              </div>
              <button type='button' className='btn btn-sm btn-icon btn-active-light-primary' onClick={onClose}>
                <i className='bi bi-x-lg'></i>
              </button>
            </div>

            <div className='modal-body px-6 pt-4 pb-2'>
              <div
                className='rounded border px-4 py-3 mb-5 text-muted'
                style={{backgroundColor: '#f9fafb', borderColor: '#f1f1f2', fontSize: '0.82rem', lineHeight: 1.5}}
              >
                {showDistanceRadius
                  ? 'Rebroadcasting will trigger an email notification to all the available interpreters. Matches on the appointment\'s existing criteria — language, gender, communication type & availability. No appointment record is modified.'
                  : 'Rebroadcasting will trigger an email notification to all qualified interpreters matching language, gender, communication type & availability. Geographic distance is not applied for this communication type. No appointment record is modified.'}
              </div>

              {showDistanceRadius && (
                <>
                  <label className='form-label fw-bold text-gray-900 mb-2'>Distance radius</label>
                  <div className='d-flex flex-wrap gap-2 mb-5'>
                    {DISTANCE_OPTIONS.map((miles) => {
                      const active = distanceMiles === miles
                      return (
                        <button
                          key={miles}
                          type='button'
                          className='btn btn-sm fw-semibold'
                          style={{
                            backgroundColor: active ? '#a3a242' : '#ffffff',
                            color: active ? '#ffffff' : '#7e8299',
                            border: `1.5px solid ${active ? '#a3a242' : '#e1e3ea'}`,
                            borderRadius: '0.5rem',
                            padding: '9px 16px',
                          }}
                          onClick={() => setDistanceMiles(miles)}
                        >
                          {miles} mi
                        </button>
                      )
                    })}
                  </div>
                </>
              )}

              <RebroadcastOption
                id='rebroadcast-ignore-service-type'
                checked={ignoreServiceType}
                onChange={setIgnoreServiceType}
                title='Ignore service type'
                tooltip={
                  <>
                    When enabled, all interpreters matching language, gender, communication type
                    {showDistanceRadius ? ', distance' : ''} &amp; availability are invited —
                    service type is not considered.
                  </>
                }
                description='Widen the pool to every qualified interpreter regardless of service type.'
              />

              {showAiAssignmentRerun && (
                <div className='mt-3'>
                  <RebroadcastOption
                    id='rebroadcast-rerun-ai-assignment'
                    checked={rerunAiAssignment}
                    onChange={setRerunAiAssignment}
                    title='Re-run AI Assignment Agent'
                    tooltip='Clears the agent’s previous SMS activity for this appointment and starts again once the new interpreter pool is ready, using the current rates.'
                    description='Recalculate profit margins and restart SMS outreach. If left unticked, any AI outreach still running for this appointment is stopped.'
                  />
                </div>
              )}
            </div>

            <div className='modal-footer border-0 justify-content-end gap-2 px-6 pb-6 pt-0'>
              <button
                type='button'
                className='btn btn-light btn-sm fw-semibold'
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-sm fw-semibold d-inline-flex align-items-center gap-2'
                style={{backgroundColor: '#a3a242', color: '#ffffff', border: 'none'}}
                onClick={handleRebroadcast}
                disabled={isSubmitting}
              >
                <i className='bi bi-broadcast-pin'></i>
                {isSubmitting ? 'Rebroadcasting...' : 'Rebroadcast'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export default RebroadcastModal
